/**
 * Client-side sample-variable substitution for message previews.
 *
 * Mirrors the backend `EmailTemplateService.applySampleVariables` exactly so the
 * SMS preview (rendered purely on the client — SMS is plain text) matches what
 * the e-mail preview shows. Keep the sample values in sync with the backend.
 */
export function substituteSampleVars(
    text: string,
    businessName?: string | null,
    lang?: string
): string {
    const en = (lang ?? '').toLowerCase().startsWith('en');
    const company = (businessName ?? '').trim() || (en ? 'Your Business' : 'Sua Empresa');
    return (text || '')
        .replace(/\{first_name\}/g, 'Maria')
        .replace(/\{last_name\}/g, 'Silva')
        .replace(/\{name\}/g, 'Maria Silva')
        .replace(/\{email\}/g, 'maria.silva@email.com')
        .replace(/\{phone\}/g, '(11) 98888-7777')
        .replace(/\{service\}/g, en ? 'Consultation' : 'Consulta')
        .replace(/\{business\}/g, company)
        .replace(/\{date\}/g, en ? '06/12/2026' : '12/06/2026')
        .replace(/\{time\}/g, en ? '5:00 PM' : '17:00');
}

/**
 * GSM-7 default alphabet (plus the common extension chars). When every character
 * of a message fits this set, carriers pack it 7 bits/char → 160 chars/segment
 * (153 when the message spans multiple concatenated segments). Otherwise the
 * message is sent as UCS-2 → 70/67 chars per segment.
 */
const GSM7 =
    "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ ÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?" +
    "¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà" +
    "^{}\\[~]|€";

function isGsm7(text: string): boolean {
    for (const ch of text) {
        if (GSM7.indexOf(ch) === -1) return false;
    }
    return true;
}

export interface SmsCount {
    chars: number;
    segments: number;
    /** Per-segment character budget (160/153 for GSM-7, 70/67 for UCS-2). */
    perSegment: number;
    encoding: 'GSM-7' | 'UCS-2';
}

/** Computes the character count and number of SMS segments for `text`. */
export function smsCount(text: string): SmsCount {
    const s = text || '';
    // Count by code points so emoji / accented chars are measured as the carrier sees them.
    const chars = Array.from(s).length;
    const gsm = isGsm7(s);
    const single = gsm ? 160 : 70;
    const multi = gsm ? 153 : 67;

    let segments: number;
    if (chars === 0) segments = 0;
    else if (chars <= single) segments = 1;
    else segments = Math.ceil(chars / multi);

    return {
        chars,
        segments,
        perSegment: segments <= 1 ? single : multi,
        encoding: gsm ? 'GSM-7' : 'UCS-2',
    };
}
